/**
 * Background tasks sagas.
 * - Boot: on auth/loginSuccess (or if a token is present at startup), fetch
 *   the first page so refresh-survival works.
 * - Load more: cursor pagination by oldest started_at.
 * - Task clicks: deeplink via deeplink_kind.
 */

import { call, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import * as api from '@/infra/api/client';
import { withSagaFlow, emitSagaPoint } from '@/features/qpl-client/saga-helpers';
import { BackgroundTask } from '@/infra/types/domain';
import {
  FETCH_TASKS_REQUESTED,
  LOAD_MORE_REQUESTED,
  TASK_CLICKED,
  fetchTasksRequested,
  fetchTasksSucceeded,
  fetchTasksFailed,
  loadMoreSucceeded,
  loadMoreFailed,
  closePopover,
  selectBackgroundTasksState,
} from './ducks';
import { navigate } from '@/infra/navigation/history';
import { DEFAULT_PAGE_SIZE as PAGE_SIZE } from '@/infra/api/pagination';

function* fetchTasksSaga(action: { type: string }): Generator {
  yield call(withSagaFlow, 'background_tasks.fetch', ['backgroundTasks'], action, function* () {
    try {
      const items = (yield call(api.listTasks, { limit: PAGE_SIZE })) as BackgroundTask[];
      yield* emitSagaPoint('api_listTasks_ok', { count: items.length });
      yield put(fetchTasksSucceeded(items, items.length === PAGE_SIZE));
    } catch (e: any) {
      yield put(fetchTasksFailed(e?.message || 'Failed to load background jobs'));
      yield* emitSagaPoint('api_listTasks_err', { message: e?.message });
    }
  });
}

function* loadMoreSaga(action: { type: string }): Generator {
  yield call(withSagaFlow, 'background_tasks.load_more', ['backgroundTasks'], action, function* () {
    try {
      const st = (yield select(selectBackgroundTasksState)) as { oldestStartedAt: string | null };
      const params: { limit: number; before?: string } = { limit: PAGE_SIZE };
      if (st.oldestStartedAt) params.before = st.oldestStartedAt;
      const items = (yield call(api.listTasks, params)) as BackgroundTask[];
      yield* emitSagaPoint('api_listTasks_ok', { count: items.length, has_cursor: !!st.oldestStartedAt });
      yield put(loadMoreSucceeded(items, items.length === PAGE_SIZE));
    } catch (e: any) {
      yield put(loadMoreFailed(e?.message || 'Failed to load more jobs'));
      yield* emitSagaPoint('api_listTasks_err', { message: e?.message });
    }
  });
}

function* taskClickedSaga(action: { type: string; payload: BackgroundTask }): Generator {
  const t = action.payload;
  yield call(withSagaFlow, 'background_tasks.task_clicked', ['backgroundTasks'], action, function* () {
    if (t.status === 'running') {
      yield* emitSagaPoint('noop_running');
      return;
    }
    yield put(closePopover());
    switch (t.deeplink_kind) {
      case 'contact_review': {
        const subjectId = t.subject_id || '';
        if (!subjectId) {
          yield* emitSagaPoint('noop_missing_subject');
          return;
        }
        yield call(navigate, `/contacts/${subjectId}/review`);
        yield* emitSagaPoint('navigated');
        return;
      }
      default:
        yield* emitSagaPoint('noop_unknown_deeplink');
        return;
    }
  });
}

/**
 * Boot watcher — fires on auth/loginSuccess. watchSessionState dispatches
 * loginSuccess after the foauth session lands on `authenticated` and
 * userinfo resolves, so page reloads with a valid session still kick off
 * the initial fetch.
 */
function* bootSaga(): Generator {
  while (true) {
    yield take('auth/loginSuccess');
    yield put(fetchTasksRequested());
  }
}

export function* backgroundTasksSaga(): Generator {
  yield takeLatest(FETCH_TASKS_REQUESTED, fetchTasksSaga);
  yield takeLatest(LOAD_MORE_REQUESTED, loadMoreSaga);
  yield takeEvery(TASK_CLICKED, taskClickedSaga);
  yield fork(bootSaga);
}
